import type { GatewayBrowserClient } from "../gateway.ts";

export type FleetResetNodeState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  fleetResetNodeBusy?: boolean;
  lastError?: string | null;
};

export type FleetResetNodeResult = {
  ok: boolean;
  dryRun?: boolean;
  mainSessionKey?: string;
  mainSessionId?: string;
  stoppedEmbeddedRuns?: number;
  abortedChatRuns?: number;
  deletedSessions?: number;
  archivedTranscripts?: number;
  clearedSubagents?: number;
  /**
   * Counts from the gateway `purgeLingeringSubagentRuns` pass that runs BEFORE
   * the registry is wiped. Surfaces how many running subagents were actually
   * stopped (vs how many lingered as orphans in the pre-fix world).
   */
  purgedLingeringSubagents?: {
    stoppedActive?: number;
    sweptSessions?: number;
    abortedChatRuns?: number;
    remainingRegistry?: number;
  };
  clearedLaneQueueTasks?: number;
  clearedFollowupTasks?: number;
  preview?: {
    sessionKeys?: string[];
  };
};

/**
 * Non-mutating preview of what a node reset would delete. The gateway refuses
 * `fleet.resetNode` without `confirm:true` (JAISIU-2186 phase-3), so the UI
 * always previews first, then confirms.
 */
export async function previewFleetNodeReset(
  state: FleetResetNodeState,
): Promise<FleetResetNodeResult | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  try {
    const res = await state.client.request<FleetResetNodeResult>("fleet.resetNode", {
      dryRun: true,
      confirm: true,
    });
    return res;
  } catch (err) {
    state.lastError = String(err);
    return null;
  }
}

export async function resetFleetNode(
  state: FleetResetNodeState,
): Promise<FleetResetNodeResult | null> {
  if (!state.client || !state.connected || state.fleetResetNodeBusy) {
    return null;
  }
  state.fleetResetNodeBusy = true;
  try {
    const res = await state.client.request<FleetResetNodeResult>("fleet.resetNode", {
      confirm: true,
    });
    return res;
  } catch (err) {
    state.lastError = String(err);
    return null;
  } finally {
    state.fleetResetNodeBusy = false;
  }
}
